import React, { FunctionComponent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import useTypedSelector from 'hooks/useTypedSelector';
import { onDelete, onGetAll } from 'redux/actions/permissions.actions';
import { permissionsSelector } from 'redux/selectors/permissions.selector';
import { history } from 'helpers/history';
import { COLORS } from 'helpers/enums/colors';

import LayoutContainer from 'containers/LayoutContainer';
import PermissionsView from 'views/PermissionsView';
import ActionButtons from 'components/commons/Table/components/ActionButtons';
import Dialog from 'components/commons/Dialog';

import { IPermission } from 'types/permissions.types';
import { ITableUpdate } from 'components/commons/Table/types';

import 'views/PermissionsView/i18n';

const PermissionsContainer: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [deleteItem, setDeleteItem] = useState<IPermission | undefined>();
  const { count, loading, table } = useTypedSelector((state) => state.permissions);
  const items = useTypedSelector(permissionsSelector);

  const actions = [
    {
      label: i18next.t<string>('PERMISSIONS_VIEW:EDIT'),
      onClick: (id: string) => history.push(`/users/permissions/${id}/edit`),
    },
    {
      label: i18next.t<string>('PERMISSIONS_VIEW:DELETE'),
      onClick: (id: string) =>
        setDeleteItem(items.find((item: IPermission) => item.id === Number(id))),
      color: COLORS.redMonza,
    },
  ];

  const columns = [
    {
      name: 'name',
      label: i18next.t<string>('PERMISSIONS_VIEW:NAME'),
    },
    {
      name: 'guardName',
      label: i18next.t<string>('PERMISSIONS_VIEW:GUARD_NAME'),
    },
    {
      name: 'id',
      label: i18next.t<string>('PERMISSIONS_VIEW:ACTIONS'),
      options: {
        customBodyRender: (value: string) => <ActionButtons id={value} items={actions} />,
        sort: false,
      },
    },
  ];

  const onTableUpdate = useCallback((data: ITableUpdate) => dispatch(onGetAll(data)), [dispatch]);

  const onDeleteClick = (isConfirmed = false) => {
    if (isConfirmed && deleteItem) dispatch(onDelete(deleteItem.id.toString()));
    setDeleteItem(undefined);
  };

  return (
    <LayoutContainer auth title={i18next.t<string>('PERMISSIONS_VIEW:TITLE')}>
      <PermissionsView
        columns={columns}
        count={count}
        data={items}
        loading={loading}
        onAddClick={() => history.push('/users/permissions/create')}
        onTableUpdate={onTableUpdate}
        table={table}
      />
      <Dialog
        message={i18next.t<string>('PERMISSIONS_VIEW:DELETE_DIALOG_MESSAGE', {
          name: `${deleteItem?.name} (${deleteItem?.guardName})`,
        })}
        onCancelClick={() => onDeleteClick()}
        onConfirmClick={() => onDeleteClick(true)}
        open={!!deleteItem}
        title={i18next.t<string>('PERMISSIONS_VIEW:DELETE_DIALOG_TITLE')}
      />
    </LayoutContainer>
  );
};

export default PermissionsContainer;
